<template>
    <div>
        <SToolbar>
            <v-icon small class="mr-1">ic-20 ic-info ic-col-grey</v-icon>
            <v-toolbar-title>{{ $t('admin') }}</v-toolbar-title>
        </SToolbar>
        <v-card class="overflow-hidden">
            <v-navigation-drawer
                v-model="showErwSuche"
                absolute
                bottom
                temporary
                right
                width="530px"
                color="kp-light-panel-bg"
            >
                <v-toolbar dense class="kp-sublime-toolbar kp-root-panel-bg" elevation="0">
                    <v-toolbar-title>{{ $t('erw_suche') }}</v-toolbar-title>
                    <v-spacer />
                    <IconBtn icon-cls="x" icon-color="grey" @click="showErwSuche = false" />
                </v-toolbar>
                <BannerEnhancedSearch
                    :initial-params="extendedSearchParams"
                    @search="onEnhancedSearch"
                    @reset="resetEnhancedSearch"
                />
            </v-navigation-drawer>
            <BaseRemoteList
                class="banner-list"
                session-key="banner-list"
                :subtitle="$t('banner')"
                :collection-class="BannerCollection"
                :headers="headers"
                :filters="filters"
                :extra-params="extraParams"
                :item-class="() => 'banner-list-row'"
                :sort-by="sortBy"
                :sort-desc="sortDesc"
                xlsx-export
                cache-data
                :new-btn="false"
                height="var(--usable-height-with-double-toolbar)"
                @loading="loading = $event"
                @edit-item="onEdit"
                @dblclick:row="onRowDblClick"
            >
                <template #toolbar-start>
                    <!-- new menu -->
                    <v-menu offset-y>
                        <template #activator="{ on, attrs }">
                            <!-- we cant use a tooltip component here because the menu is not opening at the right position -->
                            <v-btn v-bind="attrs" color="primary white--text" depressed :disabled="loading" v-on="on">
                                <KpIcon icon-cls="new" color="white" disabled-color="grey" left />
                                {{ $t('neu') }}
                            </v-btn>
                        </template>
                        <v-list dense>
                            <v-list-item v-for="(obj, type) in bannerTypes" :key="type" dense @click="onNew(type)">
                                <v-list-item-content>
                                    <v-list-item-title> {{ $t(obj.title_key) }} </v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </template>
                <template #toolbar-items>
                    |
                    <v-btn-toggle
                        v-model="toggleFilters"
                        multiple
                        class="ml-2"
                        borderless
                        tile
                        background-color="primary"
                    >
                        <IconBtn
                            value="excludeOldBanners"
                            color="primary"
                            :disabled="loading"
                            icon-cls="filter"
                            icon-color="white"
                            icon-disabled-color="grey"
                            :tooltip="$t('all_excl_old')"
                        />
                        <IconBtn
                            value="is_placeholder"
                            color="primary"
                            :disabled="loading"
                            icon-cls="info"
                            icon-color="white"
                            icon-disabled-color="grey"
                            :tooltip="$t('placeholder_filter')"
                        />
                    </v-btn-toggle>
                    |
                    <BannerTypeSelect v-model="typeFilter" class="ml-2 flex-grow-0 flex-shrink-0" />

                    <!-- Sort-Selector -->
                    <v-menu offset-y>
                        <template #activator="{ on, attrs }">
                            <v-btn class="ml-2" color="white tgk-paragraph-text" v-bind="attrs" v-on="on">
                                <KpIcon icon-cls="sortdown" color="grey" /> {{ $t('sort_by') }}
                            </v-btn>
                        </template>
                        <v-list dense>
                            <v-list-item-group :value="bannerSort" @change="onSortChange">
                                <v-list-item dense value="title">
                                    <v-list-item-icon>
                                        <KpIcon v-if="bannerSort === 'title'" icon-cls="tick" color="grey" />
                                    </v-list-item-icon>
                                    <v-list-item-title>{{ $t('titel') }}</v-list-item-title>
                                </v-list-item>
                                <v-list-item dense value="publish_from">
                                    <v-list-item-icon>
                                        <KpIcon v-if="bannerSort === 'publish_from'" icon-cls="tick" color="grey" />
                                    </v-list-item-icon>
                                    <v-list-item-title>{{ $t('pub_from') }}</v-list-item-title>
                                </v-list-item>
                                <v-list-item dense value="publish_to">
                                    <v-list-item-icon>
                                        <KpIcon v-if="bannerSort === 'publish_to'" icon-cls="tick" color="grey" />
                                    </v-list-item-icon>
                                    <v-list-item-title>{{ $t('pub_to') }}</v-list-item-title>
                                </v-list-item>
                            </v-list-item-group>
                            <v-divider />
                            <v-list-item-group :value="bannerSortDir" @change="onSortDirChange">
                                <v-list-item dense value="asc">
                                    <v-list-item-icon>
                                        <KpIcon v-if="bannerSortDir === 'asc'" icon-cls="tick" color="grey" />
                                    </v-list-item-icon>
                                    <v-list-item-title>{{ $t('asc') }}</v-list-item-title>
                                </v-list-item>
                                <v-list-item dense value="desc">
                                    <v-list-item-icon>
                                        <KpIcon v-if="bannerSortDir === 'desc'" icon-cls="tick" color="grey" />
                                    </v-list-item-icon>
                                    <v-list-item-title>{{ $t('desc') }}</v-list-item-title>
                                </v-list-item>
                            </v-list-item-group>
                        </v-list>
                    </v-menu>
                </template>

                <template #after-query-field>
                    <div
                        :class="{
                            'ml-1': true,
                            'erw-suche-indicator': showEnhancedSearchIndicator,
                        }"
                    >
                        <IconBtn
                            color="primary"
                            :disabled="loading"
                            icon-cls="advancedsearch"
                            icon-color="white"
                            icon-disabled-color="grey"
                            :tooltip="$t('erw_suche')"
                            @click="showErwSuche = true"
                        />
                    </div>
                </template>

                <template #[`item.image`]="{ item }">
                    <banner-list-image :banner="item" />
                </template>
                <template #[`item.description`]="{ item }">
                    <banner-list-description :banner="item" />
                </template>
                <template #additional-actions="{ item }">
                    <icon-btn
                        icon-cls="duplicates"
                        icon-color="grey"
                        :tooltip="$t('duplicate')"
                        @click="duplicateBanner(item)"
                    />
                </template>
            </BaseRemoteList>
        </v-card>
    </div>
</template>

<script>
import { BannerCollection, TYPE as BannerType } from '@/models/Banner';
import sessionStorageMixin from '@/mixins/session-storage';
import BannerEnhancedSearch from '@/components/banner/EnhancedSearch.vue';
import BannerListDescription from '@/components/banner/banner-list-description.vue';
import BannerListImage from '@/components/banner/banner-list-image.vue';
import BannerTypeSelect from '@/components/banner/banner-type-select.vue';
import { errToStr } from '@/helpers/tools';
import { instance as api } from '@/helpers/api';

export default {
    name: 'BannerIndex',

    components: {
        BannerEnhancedSearch,
        BannerListDescription,
        BannerListImage,
        BannerTypeSelect,
    },
    mixins: [sessionStorageMixin],

    data() {
        this.sessionKey = 'banner-index';
        const data = {
            loading: false,
            sortBy: this.getSessionValue('sortBy', ['publish_from']),
            sortDesc: this.getSessionValue('sortDesc', [true]),
            BannerCollection,
            headers: [
                { text: 'Aktionen', sortable: false, width: 80, value: 'actions' },
                { text: 'Bild', value: 'image', width: 100, sortable: false },
                { text: this.$t('description'), sortable: false, value: 'description' },
            ],
            toggleFilters: this.getSessionValue('toggleFilters') || [],
            typeFilter: this.getSessionValue('typeFilter') || null,

            extendedSearchParams: this.getSessionValue('extendedSearchParams') || {},

            // will be set when extended search btn is clicked --> initially taken from extended SearchParams
            enhancedFilters: this.getSessionValue('extendedSearchParams') || null,
            showErwSuche: false,
        };

        return data;
    },
    computed: {
        filters() {
            const filters = [];
            if (this.typeFilter) {
                filters.push({
                    property: 'type',
                    value: this.typeFilter,
                });
            }
            return filters;
        },

        bannerSort() {
            return this.sortBy?.length > 0 ? this.sortBy[0] : null;
        },

        bannerSortDir() {
            if (this.sortDesc?.length && this.sortDesc[0]) {
                return 'desc';
            }
            return 'asc';
        },

        extraParams() {
            const extraParams = {};
            const enhancedFilters = {};
            this.toggleFilters.forEach((ident) => {
                extraParams[ident] = true;
            });

            for (let key in this.enhancedFilters) {
                if (this.enhancedFilters[key]) {
                    enhancedFilters[key] = this.enhancedFilters[key];
                }
            }
            extraParams.enhancedFilters = enhancedFilters;
            return extraParams;
        },

        showEnhancedSearchIndicator() {
            let show = false;
            for (const key in this.enhancedFilters || {}) {
                if (!Array.isArray(this.enhancedFilters[key]) || this.enhancedFilters[key].length > 0) {
                    show = true;
                }
            }
            return show;
        },
    },

    watch: {
        toggleFilters(val) {
            this.setSessionValue('toggleFilters', [...val]);
        },
        typeFilter(val) {
            if (val) {
                this.setSessionValue('typeFilter', val);
            } else {
                this.clearSessionValue('typeFilter');
            }
        },
    },
    created() {
        this.bannerTypes = BannerType;
    },
    methods: {
        onEdit(banner) {
            this.openEntry(banner);
        },

        onRowDblClick(e, data) {
            if (data.item) {
                this.openEntry(data.item);
            }
        },

        openEntry(banner) {
            this.$router.push({ name: 'banner-edit', params: { id: banner.id } });
        },

        onNew(type) {
            this.$router.push({ name: 'banner-edit', params: { id: 'new' }, query: { type: type } });
        },

        onSortChange(act, old) {
            if (act !== old) {
                this.sortBy = [act];
                this.setSessionValue('sortBy', [act]);
            }
        },

        onSortDirChange(act, old) {
            if (act !== old) {
                this.sortDesc = [act === 'desc'];
                this.setSessionValue('sortDesc', [...this.sortDesc]);
            }
        },

        onEnhancedSearch(searchParams) {
            this.extendedSearchParams = searchParams;
            this.setSessionValue('extendedSearchParams', { ...this.extendedSearchParams });
            this.enhancedFilters = { ...this.extendedSearchParams };
            this.showErwSuche = false;
        },

        resetEnhancedSearch() {
            this.extendedSearchParams = {};
            this.setSessionValue('extendedSearchParams', { ...this.extendedSearchParams });
            this.enhancedFilters = { ...this.extendedSearchParams };
        },

        async duplicateBanner(item) {
            if (!item?.id) {
                return;
            }
            this.loading = true;
            try {
                const result = await api().post('/banner/duplicate', {
                    id: item.id,
                });
                if (result?.data?.banner) {
                    this.$router.push({ name: 'banner-edit', params: { id: result.data.banner.id } });
                }
            } catch (e) {
                this.$toast({ message: errToStr(e), color: 'error' });
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.banner-list {
    ::v-deep table > tbody > tr > td {
        vertical-align: top;
    }
}
</style>
